/**
 * 시나리오 타입별 실습기록(데이터) 조회 경로 — 플랫폼 API(api2)
 * 사용자 제공 표: 수강생/관리자(ins) 분기, x-class-id 필요
 */

import type {
	GetScenarioBasicInfoResponseDto,
} from '@/types/api/scenario';
import { apiPreferredKoEn } from '@/lib/i18nLocale';
import { isPlatformReloadOnlyPayload } from '@/utils/scenarioDetailResolver';
import { buildPlatformGetUrl, platformGet, PlatformApiError } from '@/services/platformApiClient';

export type ScenarioPracticeFamily = 'ls' | 'md' | 'nb' | 'acls' | 'covid' | 'ktas' | 'triage' | 'ppe' | 'generic';

const norm = (s: string) => s.toLowerCase().trim();

/**
 * `GET {lang}/scenario/data/:scenario_type/:scenario_id` — 정맥주사·채혈만 2세그먼트
 * @see medicrew-renewal-front-end `evaluation.service.ts` · `evaluation-study-result.component.ts`
 */
export function resolveScenarioDataSiTypeSegment(contentCode: string | undefined): string | null {
	if (contentCode == null || String(contentCode).trim() === '') return null;
	const c = norm(contentCode);
	if (c === 'medication') return 'iv_injection';
	if (c === 'diagnostic_test') return 'blood_draw';
	return null;
}

/** 플랫폼 플레이 기록 `content_type` — 앵귤러 `SessionMenuService.setViewType` 과 동일 신호 */
export type InferScenarioPracticeFamilyOpts = {
	platformContentType?: string;
};

/** 시나리오 스튜디오 기본정보(/info)로 플랫폼 시나리오 계열 추정 */
export function inferScenarioPracticeFamily(
	info: GetScenarioBasicInfoResponseDto | null,
	opts?: InferScenarioPracticeFamilyOpts,
): ScenarioPracticeFamily {
	const ct =
		opts?.platformContentType != null && String(opts.platformContentType).trim() !== ''
			? norm(String(opts.platformContentType))
			: '';
	/** @see medicrew-renewal-front-end `session-menu.service.ts` `setViewType` */
	if (ct === 'vr_ls') return 'ls';
	if (ct === 'vr_md') return 'md';
	if (ct === 'vr') return 'nb';
	if (ct === 'webgl') return 'ktas';

	if (!info) return 'generic';
	const infoAny = info as unknown as Record<string, unknown>;
	const scenarioCategoryFromNested = (infoAny.scenarioCategory as Record<string, unknown> | undefined)
		?.title;
	const categoryList = infoAny.scenarioCategoryList as { title?: string }[] | undefined;
	const scenarioCategoryFromList =
		Array.isArray(categoryList) && categoryList.length > 0
			? categoryList.map((c) => c?.title).filter(Boolean).join(' ')
			: undefined;
	const scenarioTypeTitle =
		(infoAny.scenarioType as Record<string, unknown> | undefined)?.title ??
		(typeof infoAny.scenarioTypeTitle === 'string' ? infoAny.scenarioTypeTitle : undefined);
	const keyLike =
		typeof infoAny.key === 'string'
			? infoAny.key
			: typeof infoAny.modeValue === 'string'
				? infoAny.modeValue
				: '';
	const modeStr = typeof infoAny.modeValue === 'string' ? infoAny.modeValue : '';

	/** `content_type` 누락 시 Learn Skills `key`(ls000001 등) 보조 */
	if (keyLike && /^ls\d{6}$/i.test(keyLike)) return 'ls';
	
	const hay = norm(
		[
			scenarioCategoryFromNested,
			scenarioCategoryFromList,
			scenarioTypeTitle,
			info.title,
			keyLike,
			modeStr,
		]
			.filter(Boolean)
			.join(' ')
	);
	if (hay.includes('acls')) return 'acls';
	if (hay.includes('covid')) return 'covid';
	if (hay.includes('ktas')) return 'ktas';
	if (hay.includes('triage') || hay.includes('중증도') || hay.includes('salt') || hay.includes('start')) {
		return 'triage';
	}
	if (hay.includes('ppe') || hay.includes('개인보호구') || hay.includes('personal protective equipment')) {
		return 'ppe';
	}
	if (hay.includes('act order') || hay.includes('액트') || hay.includes('scenario-nb') || hay.includes('_nb')) {
		return 'nb';
	}
	if (hay.includes('make decision') || hay.includes('md') || hay.includes('scenario-md')) return 'md';
	if (hay.includes('learn skill') || hay.includes('learn skills') || hay.includes('scenario-ls')) return 'ls';
	return 'generic';
}

/**
 * 실습기록 조회(데이터) — 표준 패턴만 구현. triage/na/si 등 세그먼트 많은 타입은 추후 확장.
 */
function segmentPlatformUserId(userId: number | string | undefined): string {
	if (userId == null) return '';
	if (typeof userId === 'number' && Number.isFinite(userId)) {
		return encodeURIComponent(String(Math.trunc(userId)));
	}
	const s = String(userId).trim();
	return s ? encodeURIComponent(s) : '';
}

function platformGetOpts(classId: string, accessToken?: string): { classId: string; accessToken?: string } {
	if (accessToken != null && String(accessToken).trim() !== '') {
		return { classId, accessToken: String(accessToken).trim() };
	}
	return { classId };
}

/**
 * `fetchScenarioPracticePayload`가 호출하는 api2 경로·절대 URL(로그·문서용). 실제 fetch는 하지 않음.
 */
export function getScenarioPracticePlatformGetUrl(opts: {
	lang: string;
	classId: string | number;
	scenarioId: string | number;
	/** BFF 시나리오 `key`(content_code) — 정맥주사·채혈 시 `/scenario/data/{iv_injection|blood_draw}/…` 분기 */
	contentCode?: string;
	userId?: number | string;
	isManager: boolean;
	family: ScenarioPracticeFamily;
}): { path: string; fullUrl: string } {
	const { lang, classId, scenarioId, userId, isManager, family, contentCode } = opts;
	const L = apiPreferredKoEn(lang);
	const cid = String(classId);
	const sid = String(scenarioId);
	const uid = segmentPlatformUserId(userId);
	let relPath: string;
	switch (family) {
		case 'acls':
			relPath = isManager && uid ? `${L}/scenario-acls/result/${uid}/${sid}` : `${L}/scenario-acls/result/${sid}`;
			break;
		case 'covid':
			relPath = isManager && uid ? `${L}/scenario-covid19/result/${uid}/${sid}` : `${L}/scenario-covid19/result/${sid}`;
			break;
		case 'md':
			relPath = isManager && uid ? `${L}/scenario-md/ins/data/${uid}/${sid}` : `${L}/scenario-md/data/${sid}`;
			break;
		case 'nb':
			relPath = isManager && uid ? `${L}/scenario-nb/ins/data/${uid}/${sid}` : `${L}/scenario-nb/data/${sid}`;
			break;
		case 'ls':
			relPath = isManager && uid ? `${L}/scenario-ls/ins/data/${uid}/${sid}` : `${L}/scenario-ls/data/${sid}`;
			break;
	case 'ktas':
		relPath = `${L}/scenario-ktas/data/${sid}`;
		break;
	case 'triage':
		// Triage는 학생/관리자 모두 동일한 경로 사용 (앵귤러 프로젝트와 동일)
		relPath = `${L}/scenario/data/${sid}`;
		break;
	case 'ppe':
		relPath = isManager && uid ? `${L}/ppe/result/${uid}/${sid}` : `${L}/ppe/result/${sid}`;
		break;
	default: {
		const siSeg = resolveScenarioDataSiTypeSegment(contentCode);
		relPath = siSeg ? `${L}/scenario/data/${siSeg}/${sid}` : `${L}/scenario/data/${sid}`;
		break;
	}
	}
	return { path: relPath, fullUrl: buildPlatformGetUrl(relPath, {}) };
}

export async function fetchScenarioPracticePayload(opts: {
	lang: string;
	classId: string | number;
	scenarioId: string | number;
	/** BFF `info.key` — medication·diagnostic_test 시 플랫폼 경로 분기 */
	contentCode?: string;
	/** 숫자 또는 MongoDB ObjectId 문자열 */
	userId?: number | string;
	isManager: boolean;
	family: ScenarioPracticeFamily;
	/** api2 전용 JWT — `getStudyResultPlatformAccessToken()` / 생략 시 웹 토큰 */
	accessToken?: string;
}): Promise<unknown | null> {
	const { lang, classId, scenarioId, userId, isManager, family, accessToken, contentCode } = opts;
	const cid = String(classId);
	const { path } = getScenarioPracticePlatformGetUrl({
		lang,
		classId: cid,
		scenarioId,
		contentCode,
		userId,
		isManager,
		family,
	});
	const classOpts = platformGetOpts(cid, accessToken);

	try {
		const raw = await platformGet<unknown>(path, classOpts);
		if (isPlatformReloadOnlyPayload(raw)) {
			return null;
		}
		return raw;
	} catch (e) {
		if (process.env.NODE_ENV === 'development') {
			if (e instanceof PlatformApiError) {
				console.error('[fetchScenarioPracticePayload]', path, e.status, e.data ?? e.message);
			} else {
				console.error('[fetchScenarioPracticePayload]', path, e);
			}
		}
		return null;
	}
}
